import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, deleteDoc, doc, query, orderBy, where, serverTimestamp, writeBatch } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../../utils/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getSchools, School } from '../../utils/firebase';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import InputField from '../../components/ui/InputField';
import EditIcon from '../../components/icons/EditIcon';
import TrashIcon from '../../components/icons/TrashIcon';
import CloseIcon from '../../components/icons/CloseIcon';
import CheckCircleIcon from '../../components/icons/CheckCircleIcon';
import CloseCircleIcon from '../../components/icons/CloseCircleIcon';

interface User {
  uid: string;
  email: string;
  name: string;
  role: 'student' | 'teacher' | 'schoolAdmin' | 'superAdmin';
  schoolId?: string;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

const SecurityManagement: React.FC = () => {
  const { userRole } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [strippingPasswords, setStrippingPasswords] = useState(false);
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState({
    role: 'student' as 'student' | 'teacher' | 'schoolAdmin' | 'superAdmin',
    isActive: true
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSchoolId, setSelectedSchoolId] = useState<string>('all');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [sortOrder, setSortOrder] = useState<'name-asc' | 'name-desc' | 'date-desc' | 'date-asc'>('date-desc');

  useEffect(() => {
    fetchUsers();
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    try {
      const schoolsData = await getSchools();
      setSchools(schoolsData);
    } catch (error) {
      console.error('학교 목록 가져오기 오류:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const usersList = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      })) as User[];
      
      setUsers(usersList);
    } catch (error) {
      console.error('사용자 목록 가져오기 오류:', error);
      setError('사용자 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleStripStoredPasswords = async () => {
    if (userRole !== 'superAdmin') {
      setError('최고 관리자만 실행할 수 있습니다.');
      return;
    }
    if (!window.confirm('학생 문서에 남아 있는 비밀번호 필드를 삭제합니다. 로그인 비밀번호 자체는 바뀌지 않습니다.')) {
      return;
    }
    try {
      setStrippingPasswords(true);
      setError(null);
      setSuccess(null);
      if (auth.currentUser) await auth.currentUser.getIdToken(true);
      const strip = httpsCallable<Record<string, never>, { cleared?: number }>(
        getFunctions(getApp()),
        'stripStoredPasswords'
      );
      const result = await strip({});
      const cleared = Number(result.data?.cleared ?? 0);
      setSuccess(cleared > 0 ? `저장된 비밀번호 필드 ${cleared}건을 삭제했습니다.` : '삭제할 비밀번호 필드가 없습니다.');
    } catch (stripError) {
      console.error('비밀번호 필드 삭제 오류:', stripError);
      setError(stripError instanceof Error ? stripError.message : '비밀번호 필드 삭제에 실패했습니다.');
    } finally {
      setStrippingPasswords(false);
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setUserForm({
      role: user.role,
      isActive: user.isActive
    });
    setOpenEditDialog(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    try {
      setLoading(true);
      setError(null);

      // users 컬렉션 업데이트
      await updateDoc(doc(db, 'users', editingUser.uid), {
        role: userForm.role,
        isActive: userForm.isActive,
        updatedAt: serverTimestamp()
      });

      // teachers 컬렉션도 업데이트 (있는 경우)
      if (editingUser.schoolId) {
        try {
          const teacherRef = doc(db, `schools/${editingUser.schoolId}/teachers/${editingUser.uid}`);
          const teacherDoc = await getDocs(query(collection(db, `schools/${editingUser.schoolId}/teachers`), where('uid', '==', editingUser.uid)));
          if (!teacherDoc.empty) {
            await updateDoc(teacherRef, {
              role: userForm.role,
              isActive: userForm.isActive,
              updatedAt: serverTimestamp()
            });
          }
        } catch (err) {
          // teachers 컬렉션에 없을 수 있으므로 무시
          console.log('Teachers 컬렉션 업데이트 스킵:', err);
        }
      }

      setSuccess('사용자 정보가 수정되었습니다.');
      setOpenEditDialog(false);
      setEditingUser(null);
      await fetchUsers();
    } catch (error) {
      console.error('사용자 정보 수정 오류:', error);
      setError('사용자 정보 수정 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        isActive: !user.isActive,
        updatedAt: serverTimestamp()
      });
      await fetchUsers();
    } catch (error) {
      console.error('사용자 상태 변경 오류:', error);
      setError('사용자 상태 변경 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!window.confirm(`정말로 ${user.name} 사용자를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // users 컬렉션에서 삭제
      await deleteDoc(doc(db, 'users', user.uid));

      // teachers 컬렉션에서도 삭제 (있는 경우)
      if (user.schoolId) {
        try {
          const teacherRef = doc(db, `schools/${user.schoolId}/teachers/${user.uid}`);
          await deleteDoc(teacherRef);
        } catch (err) {
          // teachers 컬렉션에 없을 수 있으므로 무시
          console.log('Teachers 컬렉션 삭제 스킵:', err);
        }
      }

      setSuccess('사용자가 삭제되었습니다.');
      setSelectedUserIds(new Set());
      await fetchUsers();
    } catch (error) {
      console.error('사용자 삭제 오류:', error);
      setError('사용자 삭제 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedUserIds.size === filteredUsers.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(filteredUsers.map(user => user.uid)));
    }
  };

  const handleBatchDelete = async () => {
    if (selectedUserIds.size === 0) return;

    const selectedUsers = filteredUsers.filter(user => selectedUserIds.has(user.uid));
    const userNames = selectedUsers.map(u => u.name).join(', ');
    
    if (!window.confirm(`정말로 선택한 ${selectedUserIds.size}명의 사용자를 삭제하시겠습니까?\n\n${userNames}\n\n이 작업은 되돌릴 수 없습니다.`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const batch = writeBatch(db);
      const deletePromises: Promise<void>[] = [];

      // users 컬렉션에서 삭제
      selectedUsers.forEach(user => {
        batch.delete(doc(db, 'users', user.uid));
        
        // teachers 컬렉션에서도 삭제 (있는 경우)
        if (user.schoolId) {
          const teacherRef = doc(db, `schools/${user.schoolId}/teachers/${user.uid}`);
          deletePromises.push(
            deleteDoc(teacherRef).catch(err => {
              // teachers 컬렉션에 없을 수 있으므로 무시
              console.log(`Teachers 컬렉션 삭제 스킵 (${user.uid}):`, err);
            })
          );
        }
      });

      // users 컬렉션 배치 삭제 실행
      await batch.commit();
      
      // teachers 컬렉션 삭제 실행 (병렬 처리)
      await Promise.all(deletePromises);

      setSuccess(`${selectedUserIds.size}명의 사용자가 삭제되었습니다.`);
      setSelectedUserIds(new Set());
      await fetchUsers();
    } catch (error) {
      console.error('일괄 삭제 오류:', error);
      setError('사용자 일괄 삭제 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'student': return '학생';
      case 'teacher': return '교사';
      case 'schoolAdmin': return '학교 관리자';
      case 'superAdmin': return '최고 관리자';
      default: return role;
    }
  };

  const getRoleBadge = (role: string) => {
    const styles = {
      student: { bg: '#E3F2FD', color: '#1976D2' },
      teacher: { bg: '#F3E5F5', color: '#7B1FA2' },
      schoolAdmin: { bg: '#FFF3E0', color: '#F57C00' },
      superAdmin: { bg: '#FFEBEE', color: '#D32F2F' }
    };
    const style = styles[role as keyof typeof styles] || styles.student;
    
    return (
      <div style={{
        display: 'inline-flex',
        padding: '4px 12px',
        borderRadius: '12px',
        backgroundColor: style.bg,
        color: style.color,
        fontSize: '12px',
        fontWeight: 500
      }}>
        {getRoleLabel(role)}
      </div>
    );
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSchool = 
      selectedSchoolId === 'all' || 
      user.schoolId === selectedSchoolId ||
      (!user.schoolId && selectedSchoolId === 'none');
    
    return matchesSearch && matchesSchool;
  }).sort((a, b) => {
    switch (sortOrder) {
      case 'name-asc':
        return a.name.localeCompare(b.name, 'ko');
      case 'name-desc':
        return b.name.localeCompare(a.name, 'ko');
      case 'date-asc':
        const dateA = a.createdAt?.getTime() || 0;
        const dateB = b.createdAt?.getTime() || 0;
        return dateA - dateB;
      case 'date-desc':
      default:
        const dateADesc = a.createdAt?.getTime() || 0;
        const dateBDesc = b.createdAt?.getTime() || 0;
        return dateBDesc - dateADesc;
    }
  });

  const getSchoolName = (schoolId?: string) => {
    if (!schoolId) return '-';
    const school = schools.find(s => s.id === schoolId);
    return school ? school.name : schoolId;
  };

  if (loading && users.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px' 
      }}>
        <div style={{ fontSize: '16px', color: 'var(--Color-text-normal, #161616)' }}>
          로딩 중...
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* 헤더 */}
      <div
        style={{
          fontSize: '24px',
          fontWeight: 600,
          lineHeight: '36px',
          color: 'var(--Color-text-strong, #0D0D0D)'
        }}
      >
        보안 관리
      </div>

      {userRole === 'superAdmin' && (
        <Card padding="16px 20px">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ fontSize: '14px', color: 'var(--Color-text-normal, #161616)', lineHeight: '21px' }}>
              학생 문서에 평문으로 남아 있는 비밀번호 필드를 지웁니다. 로그인에 쓰는 비밀번호는 그대로입니다.
            </div>
            <Button
              variant="outline"
              size="medium"
              width="fit-content"
              disabled={strippingPasswords}
              onClick={handleStripStoredPasswords}
            >
              {strippingPasswords ? '삭제 중...' : '저장된 비밀번호 필드 삭제'}
            </Button>
          </div>
        </Card>
      )}

      {/* 알림 메시지 */}
      {error && (
        <Card
          padding="12px 16px"
          background="#FFF5F5"
          style={{ border: '1px solid #FFE0E0' }}
        >
          <div style={{ fontSize: '14px', color: '#FF3838' }}>{error}</div>
        </Card>
      )}

      {success && (
        <Card
          padding="12px 16px"
          background="#F0FFF4"
          style={{ border: '1px solid #C6F6D5' }}
        >
          <div style={{ fontSize: '14px', color: '#38A169' }}>{success}</div>
        </Card>
      )}

      {/* 검색 및 필터 */}
      <Card padding="16px 20px" gap="16px">
        <InputField
          label="사용자 검색"
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="이름 또는 이메일로 검색"
          width="100%"
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#161616',
              marginBottom: '8px'
            }}>
              학교 필터
            </label>
            <select
              value={selectedSchoolId}
              onChange={(e) => setSelectedSchoolId(e.target.value)}
              style={{
                width: '100%',
                height: '48px',
                padding: '12px 16px',
                fontSize: '14px',
                color: '#161616',
                backgroundColor: 'white',
                border: '1.2px solid #F0F0F0',
                borderRadius: '8px',
                outline: 'none',
                cursor: 'pointer',
                fontFamily: 'Pretendard, sans-serif'
              }}
            >
              <option value="all">전체</option>
              <option value="none">학교 없음</option>
              {schools.map(school => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#161616',
              marginBottom: '8px'
            }}>
              정렬
            </label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as any)}
              style={{
                width: '100%',
                height: '48px',
                padding: '12px 16px',
                fontSize: '14px',
                color: '#161616',
                backgroundColor: 'white',
                border: '1.2px solid #F0F0F0',
                borderRadius: '8px',
                outline: 'none',
                cursor: 'pointer',
                fontFamily: 'Pretendard, sans-serif'
              }}
            >
              <option value="date-desc">가입일 최신순</option>
              <option value="date-asc">가입일 오래된순</option>
              <option value="name-asc">이름 가나다순</option>
              <option value="name-desc">이름 역순</option>
            </select>
          </div>
        </div>
      </Card>

      {/* 일괄 삭제 버튼 */}
      {selectedUserIds.size > 0 && (
        <Card padding="16px 20px" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#FFF5F5',
          border: '1px solid #FFE0E0'
        }}>
          <div style={{ fontSize: '14px', color: 'var(--Color-text-strong, #0D0D0D)', fontWeight: 500 }}>
            {selectedUserIds.size}명 선택됨
          </div>
          <Button
            variant="cancel"
            onClick={handleBatchDelete}
            disabled={loading}
            style={{ backgroundColor: '#FF3838', color: 'white' }}
          >
            선택한 사용자 삭제
          </Button>
        </Card>
      )}

      {/* 사용자 목록 테이블 */}
      {filteredUsers.length === 0 ? (
        <Card padding="40px" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: 'var(--Color-text-normal, #161616)' }}>
            {searchTerm ? '검색 결과가 없습니다.' : '등록된 사용자가 없습니다.'}
          </div>
        </Card>
      ) : (
        <Card padding="0" style={{ overflow: 'hidden', width: '100%' }}>
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table style={{ width: '100%', minWidth: '900px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--Color-fill-hover, #F5F5F5)' }}>
                  <th style={{
                    padding: '12px 20px',
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    whiteSpace: 'nowrap',
                    width: '48px'
                  }}>
                    <input
                      type="checkbox"
                      checked={filteredUsers.length > 0 && selectedUserIds.size === filteredUsers.length}
                      onChange={handleSelectAll}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer'
                      }}
                    />
                  </th>
                  <th style={{
                    padding: '12px 12px',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    whiteSpace: 'nowrap'
                  }}>
                    이름
                  </th>
                  <th style={{
                    padding: '12px 12px',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    minWidth: '180px'
                  }}>
                    이메일
                  </th>
                  <th style={{
                    padding: '12px 20px',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    whiteSpace: 'nowrap'
                  }}>
                    학교
                  </th>
                  <th style={{
                    padding: '12px 20px',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    whiteSpace: 'nowrap'
                  }}>
                    역할
                  </th>
                  <th style={{
                    padding: '12px 20px',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    whiteSpace: 'nowrap'
                  }}>
                    상태
                  </th>
                  <th style={{
                    padding: '12px 20px',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    whiteSpace: 'nowrap'
                  }}>
                    가입일
                  </th>
                  <th style={{
                    padding: '12px 20px',
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    whiteSpace: 'nowrap'
                  }}>
                    관리
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.uid} style={{
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    backgroundColor: selectedUserIds.has(user.uid) ? '#FFF5F5' : 'transparent'
                  }}>
                    <td style={{
                      padding: '12px 20px',
                      textAlign: 'center',
                      whiteSpace: 'nowrap'
                    }}>
                      <input
                        type="checkbox"
                        checked={selectedUserIds.has(user.uid)}
                        onChange={() => handleSelectUser(user.uid)}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer'
                        }}
                      />
                    </td>
                    <td style={{
                      padding: '12px 12px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      whiteSpace: 'nowrap'
                    }}>
                      {user.name}
                    </td>
                    <td style={{
                      padding: '12px 12px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      minWidth: '180px'
                    }}>
                      {user.email}
                    </td>
                    <td style={{
                      padding: '12px 20px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      whiteSpace: 'nowrap'
                    }}>
                      {getSchoolName(user.schoolId)}
                    </td>
                    <td style={{
                      padding: '12px 20px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      whiteSpace: 'nowrap'
                    }}>
                      {getRoleBadge(user.role)}
                    </td>
                    <td style={{
                      padding: '12px 20px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      whiteSpace: 'nowrap'
                    }}>
                      {user.isActive ? (
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          backgroundColor: '#F0FFF4',
                          color: '#38A169',
                          fontSize: '12px',
                          fontWeight: 500
                        }}>
                          <CheckCircleIcon width={14} height={14} color="#38A169" />
                          활성
                        </div>
                      ) : (
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          backgroundColor: '#FFF5F5',
                          color: '#FF3838',
                          fontSize: '12px',
                          fontWeight: 500
                        }}>
                          <CloseCircleIcon width={14} height={14} color="#FF3838" />
                          비활성
                        </div>
                      )}
                    </td>
                    <td style={{
                      padding: '12px 20px',
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      whiteSpace: 'nowrap'
                    }}>
                      {user.createdAt ? user.createdAt.toLocaleDateString('ko-KR') : '-'}
                    </td>
                    <td style={{
                      padding: '12px 20px',
                      textAlign: 'center',
                      whiteSpace: 'nowrap'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          type="button"
                          onClick={() => handleEditUser(user)}
                          style={{
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--Color-fill-hover, #F5F5F5)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title="수정"
                        >
                          <EditIcon width={16} height={16} color="#4C4C4C" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleUserStatus(user)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: 'none',
                            backgroundColor: user.isActive ? '#FFF5F5' : '#F0FFF4',
                            color: user.isActive ? '#FF3838' : '#38A169',
                            fontSize: '12px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                          }}
                          title={user.isActive ? '비활성화' : '활성화'}
                        >
                          {user.isActive ? '비활성화' : '활성화'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteUser(user)}
                          style={{
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFF5F5';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title="삭제"
                        >
                          <TrashIcon width={14} height={16} color="#FF3838" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* 사용자 수정 다이얼로그 */}
      {openEditDialog && editingUser && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            fontFamily: 'Pretendard, sans-serif'
          }}
          onClick={() => {
            setOpenEditDialog(false);
            setEditingUser(null);
          }}
        >
          <div
            style={{
              width: '90%',
              maxWidth: '500px',
              padding: '0',
              borderRadius: '16px',
              background: 'var(--Color-background-normal, white)',
              boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
              position: 'relative',
              overflow: 'hidden',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          >
            {/* 다이얼로그 헤더 */}
            <div style={{
              padding: '32px',
              borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{
                fontSize: '20px',
                fontWeight: 600,
                color: 'var(--Color-text-strong, #0D0D0D)'
              }}>
                사용자 권한 수정
              </div>
              <button
                type="button"
                onClick={() => {
                  setOpenEditDialog(false);
                  setEditingUser(null);
                }}
                style={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--Color-fill-hover, #F5F5F5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <CloseIcon width={20} height={20} />
              </button>
            </div>

            {/* 다이얼로그 내용 */}
            <div style={{
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              <div>
                <div style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'var(--Color-text-normal, #161616)',
                  marginBottom: '8px'
                }}>
                  이름
                </div>
                <div style={{
                  fontSize: '14px',
                  color: 'var(--Color-text-strong, #0D0D0D)'
                }}>
                  {editingUser.name}
                </div>
              </div>

              <div>
                <div style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'var(--Color-text-normal, #161616)',
                  marginBottom: '8px'
                }}>
                  이메일
                </div>
                <div style={{
                  fontSize: '14px',
                  color: 'var(--Color-text-strong, #0D0D0D)'
                }}>
                  {editingUser.email}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#161616',
                  marginBottom: '8px'
                }}>
                  역할
                </label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm(prev => ({ ...prev, role: e.target.value as any }))}
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#161616',
                    backgroundColor: 'white',
                    border: '1.2px solid #F0F0F0',
                    borderRadius: '8px',
                    outline: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Pretendard, sans-serif'
                  }}
                >
                  <option value="student">학생</option>
                  <option value="teacher">교사</option>
                  <option value="schoolAdmin">학교 관리자</option>
                  <option value="superAdmin">최고 관리자</option>
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="checkbox"
                  checked={userForm.isActive}
                  onChange={(e) => setUserForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer'
                  }}
                />
                <label style={{
                  fontSize: '14px',
                  color: 'var(--Color-text-normal, #161616)',
                  cursor: 'pointer'
                }}>
                  계정 활성화
                </label>
              </div>
            </div>

            {/* 다이얼로그 푸터 */}
            <div style={{
              padding: '24px 32px',
              borderTop: '1px solid var(--Color-line-normal, #F0F0F0)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <Button
                variant="cancel"
                onClick={() => {
                  setOpenEditDialog(false);
                  setEditingUser(null);
                }}
                disabled={loading}
              >
                취소
              </Button>
              <Button
                variant="primary"
                onClick={handleUpdateUser}
                disabled={loading}
              >
                {loading ? '처리 중...' : '수정'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityManagement;
